import { Injectable, signal, computed, inject } from '@angular/core';
import { ConfirmationService, MessageService } from 'primeng/api';
import { environment } from 'src/environments/environment';
import {
  ProgramacionServicio,
  TipoCita,
  ProgramacionServicioRequest,
  UnidadNegocio
} from 'src/app/domain/model/agenda/agenda';
import {
  ConfiguracionCita,
  DEFAULT_CONFIGURACION_CITA,
  convertirFestivosADates
} from 'src/app/domain/model/agenda/configuracion-cita';
import { ContactoPersonaRepository } from 'src/app/domain/service/persona/contacto-persona-repository';
import { BeneficiarioRepository } from 'src/app/domain/service/beneficiario/beneficiario-repository';
import { NomenclaturaRepository } from 'src/app/domain/service/nomenclatura/nomenclatura-repository';
import { NomenclaturaDto } from 'src/app/domain/shared/models/nomenclatura';
import { AgendaRepository } from 'src/app/domain/service/agenda/agenda-repository';
import { UsuarioStateService } from 'src/app/infrastructure/state/usuario-state.service';
import {
  SCHEDULING_LABELS,
  SCHEDULING_TOOLTIPS,
  TIPOS_CITA_IDS,
  CAMPOS_POR_TIPO_CITA,
  CATALOGO_GRUPOS_ESTUDIO,
  CATALOGO_TIPOS_FORMACION,
  CATALOGO_TIPOS_INVESTIGACION,
  ESTADOS_EJECUTADOS
} from 'src/app/common/scheduling-constants';
import {
  ESTADO_CITA,
  MAX_OBSERVACIONES_CITA
} from 'src/app/common/constants-ejb';
import { redondearCantidad } from 'src/app/common/cantidad.utils';
import {
  esPersonaNoEncontrada,
  MENSAJE_PERSONA_DESACTUALIZADA
} from 'src/app/common/http-error-utils';
import {
  REGEX_ALPHANUMERIC_EXTENDED,
  REGEX_EMAIL
} from 'src/app/common/input-utils';
import {
  ContactoCentroTrabajo,
  AccionPaginacionContactos
} from '../components/contactos-trabajo-dialog.component';
import { ContactosCursorPager } from 'src/app/presentation/shared/contactos/contactos-cursor-pager';
import { DatosPersona } from '../components/cancelar-cita-dialog.component';
import {
  resolverVisibilidadContactos,
  VisibilidadContactos
} from './cita-visibilidad-contactos';
import { TransporteData } from '../components/panel-transporte.component';

/**
 * Convierte el código de tipo de identificación de {@code TPSC_PERSONAS}
 * (letra del DNI: C, E, N, P) a la descripción que espera el dropdown
 * readonly del diálogo de cancelación de cita.
 */
function mapTipoIdentificacion(codigo?: string): string {
  const c = codigo?.toUpperCase();
  switch (c) {
    case 'C': return 'CEDULA DE CIUDADANIA';
    case 'E': return 'CEDULA DE EXTRANJERIA';
    case 'N': return 'NIT';
    case 'P': return 'PASAPORTE';
    default: return 'CEDULA DE CIUDADANIA';
  }
}

/**
 * Modos de operación - Migrado desde Constants.TOKEN_*
 */
export type CitaMode = 'view' | 'edit' | 'create';

/**
 * Interfaz de configuración de visibilidad de campos
 */
export interface CitaVisibilityConfig {
  mostrarContactos: boolean;
  mostrarDireccion: boolean;
  mostrarSincronizacionOutlook: boolean;
  mostrarEnlaceTeams: boolean;
  mostrarBeneficiario: boolean;
  mostrarTarea: boolean;
  mostrarCentroTrabajo: boolean;
  mostrarVirtual: boolean;
  mostrarGrupoEstudio: boolean;
  mostrarFormacionTecnica: boolean;
  mostrarInvestigacion: boolean;
  mostrarUnidadNegocio: boolean;
  mostrarPPR: boolean;
  observacionObligatoria: boolean;
  ocultarPeriodicidad: boolean;
  mostrarTransporte: boolean;
  mostrarCantidadProgramada: boolean;
  mostrarAccordionDetalleOrden: boolean;
  mostrarAccordionCumplimiento: boolean;
  mostrarGuardar: boolean;
  mostrarCancelar: boolean;
  mostrarCumplir: boolean;
  mostrarEstado: boolean;
}

/**
 * Interfaz de configuración de habilitación de campos
 */
export interface CitaHabilitacionConfig {
  habilitarCampos: boolean;
  habilitarTipoCita: boolean;
  habilitarCantidad: boolean;
  habilitarPeriodicidad: boolean;
  habilitarGuardar: boolean;
  habilitarDuracion: boolean;
}

/**
 * Interfaz del formulario de cita
 */
export interface CitaFormModel {
  nmconsecutivoTipoCita: number;
  fechaCita: Date | null;
  horaInicio: Date | null;
  duracion: Date | null;
  nmcantidadProgramada: number;
  dsobservaciones: string;
  nmconsecutivoContacto: number | null;
  dsnombreContacto: string;
  dstelefonoContacto: string;
  dscelularContacto: string;
  dsemailContacto: string;
  dsdireccion: string;
  tipoperiodicidad: string | null;
  nmrepeticiones: number;
  fechaFinPeriodicidad: Date | null;
  snOutlook: string;
  snEnlaceTeams: string;
  nmconsecutivoBeneficiario: number | null;
  dscontratoBeneficiario: string;
  dsidentificacionBeneficiario: string;
  dsnombreBeneficiario: string;
  nmconsecutivoTarea: number | null;
  dsnombreTarea: string;
  nmconsecutivoCentroTrabajo: number | null;
  dsnombreCentroTrabajo: string;
  nmconsecutivoPPR: number | null;
  dsnombrePPR: string;
  dscorreoPPR: string;
  dsnombreContactoPPR: string;
  dscorreoContactoPPR: string;
  snvirtual: 'S' | 'N';
  cdmunicipioOrigen: string;
  dsmunicipioOrigen: string;
  cdmunicipioEntrega: string;
  dsmunicipioEntrega: string;
  cdpoblacion: string;
  dspoblacion: string;
  snpagoTransporteEmpleado: 'S' | 'N';
  ptvalorTransporte: number | null;
  snpagoTransporteAdicional: 'S' | 'N';
  ptvalorTransporteAdicional: number | null;
  dsobservacionTransporteAdicional: string;
  nmgrupoEstudio: number | null;
  dslineaEstudio: string;
  dstema: string;
  cdtipoFormacion: string | null;
  cdtipoInvestigacion: string | null;
  nmconsecutivoUnidadNegocio: number | null;
  dsunidadNegocio: string;

  // Campo para citas de órdenes de servicio (CAMPO/INFORME)
  nmconsecOrdenServDetalle: number | null;
}

/**
 * CitaFacade
 *
 * Facade que maneja toda la lógica del diálogo de cita.
 * Extrae la complejidad del componente para facilitar mantenimiento y testing.
 *
 * @author Emerson Rodriguez - Ingeneo
 */
@Injectable()
export class CitaFacade {
  // === Constantes del legacy para el template ===
  readonly labels = SCHEDULING_LABELS;
  readonly tooltips = SCHEDULING_TOOLTIPS;
  readonly TIPOS_CITA = TIPOS_CITA_IDS;

  // === Catálogos ===
  /**
   * Periodicidades dinámicas desde TPSC_PARAMETROS_GENERALES.
   * Legacy: Utilidades.getOptionsList(Constants.LIST_TYPE_PERIODICITY)
   * Se cargan desde la configuración del backend; si no se ha cargado aún, usa defaults.
   */
  readonly periodicidades = computed(() => {
    const config = this.configuracion();
    return config?.periodicidades ?? [];
  });
  readonly gruposEstudio = CATALOGO_GRUPOS_ESTUDIO;
  readonly tiposFormacion = [...CATALOGO_TIPOS_FORMACION];
  readonly tiposInvestigacion = [...CATALOGO_TIPOS_INVESTIGACION];

  // === Estado ===
  readonly tipoCitaSeleccionado = signal(0);
  readonly virtualSeleccionado = signal<'S' | 'N'>('S');
  /** Espejo reactivo de form.snEnlaceTeams: la visibilidad de Contactos depende de él. */
  readonly enlaceTeamsSeleccionado = signal<'S' | 'N'>('N');
  readonly datosProgramador = signal<DatosPersona | null>(null);
  readonly loadingContactos = signal(false);
  readonly loadingFormulario = signal(false);
  // Origen de los contactos mostrados en el diálogo (legacy: centro de trabajo o, si no hay, persona)
  private origenContactos: { nmconsecCt: number | null; cdpersona: number | null } = {
    nmconsecCt: null,
    cdpersona: null
  };
  /** Origen con el que se abrió el listado; se reutiliza al crear o modificar (legacy). */
  get origenContactosActual(): { nmconsecCt: number | null; cdpersona: number | null } {
    return { ...this.origenContactos };
  }
  // Paginación REAL en servidor: en memoria solo vive la página actual
  readonly contactosPager = new ContactosCursorPager((cursor, size, direction) => {
    const { nmconsecCt, cdpersona } = this.origenContactos;
    if (nmconsecCt) {
      return this.contactoRepository.getByCentroTrabajoPaged(nmconsecCt, cursor, size, direction);
    }
    if (cdpersona) {
      return this.contactoRepository.getByPersonaPaged(cdpersona, true, cursor, size, direction);
    }
    // Sin centro de trabajo ni persona: lista vacía (legacy)
    return Promise.resolve({
      content: [],
      size,
      nextCursor: null,
      previousCursor: null,
      hasNext: false,
      hasPrevious: false,
      totalElements: 0
    });
  });
  readonly contactosCentroTrabajo = computed<ContactoCentroTrabajo[]>(() =>
    this.contactosPager.items().map((c) => ({
      nmconsecutivo: c.nmconsecutivo,
      cdpersona: c.cdpersona,
      dsnombre: c.dsnombre || '',
      dscargo: c.dscargo || c.dscargo_desc,
      dstelefonoFijo: c.dstelefono,
      dstelefonoCelular: c.dscelular,
      dsemail: c.dsemail
    }))
  );
  readonly paginacionContactos = this.contactosPager.paginacion;

  // Configuración de citas (Legacy: GeneralParameters; festivos desde T094_DIAS_FESTIVOS)
  readonly configuracion = signal<ConfiguracionCita>(
    DEFAULT_CONFIGURACION_CITA
  );
  readonly festivos = computed(() =>
    convertirFestivosADates(this.configuracion()?.festivos ?? [])
  );
  readonly loadingConfiguracion = signal(false);
  /** true si la última carga de configuración falló: festivos y límite no son los reales. */
  readonly configuracionError = signal(false);
  private cargaConfiguracionEnCurso: Promise<void> | null = null;

  // Nomenclaturas para dirección
  readonly tiposVia = signal<NomenclaturaDto[]>([]);
  readonly apendices = signal<NomenclaturaDto[]>([]);
  readonly cardinalidades = signal<NomenclaturaDto[]>([]);

  /** Flag para actualizar citas futuras en programación periódica */
  snModifyPeriodicity: 'S' | 'N' = 'N';

  /** Flag para evitar mostrar el diálogo de periodicidad más de una vez por apertura de cita */
  private _periodicidadDialogoRespondido = false;

  /** Signal que indica que hay un confirm de periodicidad pendiente de respuesta */
  readonly pendienteConfirmPeriodicidad = signal(false);

  /** Flag para mostrar contacto adicional PPR */
  mostrarContactoAdicionalPPR = false;

  /** Fecha default para duración: 00:00 */
  readonly defaultDuracion = (() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  })();

  // === Formulario ===
  form: CitaFormModel = this.getFormInicial();

  // === Datos de transporte ===
  transporteData: TransporteData = this.getTransporteDataInicial();

  // === Referencias para computeds (se establecen desde el componente) ===
  private _cita: ProgramacionServicio | null = null;
  private _mode: CitaMode = 'view';
  private _tiposCita: TipoCita[] = [];

  // Fuente de verdad para el panel del programador: TPSC_PERSONAS via
  // GET /api/servicios/usuario/sesion (cacheado en UsuarioStateService).
  private readonly usuarioState = inject(UsuarioStateService);

  constructor(
    private readonly confirmationService: ConfirmationService,
    private readonly messageService: MessageService,
    private readonly contactoRepository: ContactoPersonaRepository,
    private readonly beneficiarioRepository: BeneficiarioRepository,
    private readonly nomenclaturaRepository: NomenclaturaRepository,
    private readonly agendaRepository: AgendaRepository
  ) {}

  // === Setters para datos del componente ===
  setCita(cita: ProgramacionServicio | null): void {
    this._cita = cita;
  }

  setMode(mode: CitaMode): void {
    this._mode = mode;
  }

  setTiposCita(tipos: TipoCita[]): void {
    this._tiposCita = tipos;
  }

  get cita(): ProgramacionServicio | null {
    return this._cita;
  }

  get mode(): CitaMode {
    return this._mode;
  }

  get tiposCita(): TipoCita[] {
    return this._tiposCita;
  }

  // === Carga de datos iniciales ===

  async loadNomenclaturas(): Promise<void> {
    try {
      const [tiposViaRes, apendicesRes, cardinalidadesRes] = await Promise.all([
        this.nomenclaturaRepository.listarTiposVia(),
        this.nomenclaturaRepository.listarApendices(),
        this.nomenclaturaRepository.listarCardinalidades()
      ]);
      this.tiposVia.set(tiposViaRes || []);
      this.apendices.set(apendicesRes || []);
      this.cardinalidades.set(cardinalidadesRes || []);
    } catch (error) {
      if (!environment.production) {
        console.error('Error cargando nomenclaturas:', error);
      }
    }
  }

  /**
   * Carga la configuración de citas desde el backend.
   *
   * Legacy: GeneralParameters.getParameterValue()
   * - CITA_MINIMA_DURACION (default: 0)
   * - CITA_MAXIMA_DURACION (default: 24)
   * - Festivos para bloquear en el calendario (T094_DIAS_FESTIVOS)
   *
   * Uso:
   * - `configuracion()` - Signal con la configuración completa
   * - `festivos()` - Computed con los festivos como Date[] para [disabledDates] de p-calendar
   * - `configuracionError()` - true si la carga falló; los valores son los de por defecto y no
   *   sirven para decidir si una cita es tardía
   *
   * Si ya hay una carga en curso, espera a que termine en lugar de salir de inmediato.
   */
  async cargarConfiguracion(): Promise<void> {
    if (this.loadingConfiguracion()) {
      await this.cargaConfiguracionEnCurso;
      return;
    }

    this.loadingConfiguracion.set(true);
    this.configuracionError.set(false);
    this.cargaConfiguracionEnCurso = this.ejecutarCargaConfiguracion();
    try {
      await this.cargaConfiguracionEnCurso;
    } finally {
      this.cargaConfiguracionEnCurso = null;
      this.loadingConfiguracion.set(false);
    }
  }

  private async ejecutarCargaConfiguracion(): Promise<void> {
    try {
      const config = await this.agendaRepository.obtenerConfiguracion();
      if (config) {
        this.configuracion.set(config);
        return;
      }
      this.configuracion.set(DEFAULT_CONFIGURACION_CITA);
      this.informarErrorConfiguracion();
    } catch (error) {
      if (!environment.production) {
        console.error(
          '[FESTIVOS] Error cargando configuración de citas:',
          error
        );
      }
      // Valores por defecto solo para duración mínima/máxima; no sirven para decidir tardanza
      this.configuracion.set(DEFAULT_CONFIGURACION_CITA);
      this.informarErrorConfiguracion();
    }
  }

  private informarErrorConfiguracion(): void {
    this.configuracionError.set(true);
    this.messageService.add({
      severity: 'error',
      summary: 'Error',
      detail:
        'No se pudo cargar la configuración de citas (festivos y límite de días). No es posible validar si la cita es tardía; intente nuevamente.',
      life: 5000
    });
  }

  async cargarIdentidadProgramador(): Promise<void> {
    return this.cargarDatosProgramador();
  }

  async cargarDatosProgramador(): Promise<void> {
    try {
      // Legacy: IdentificationViewImpl lee la fila real de TPSC_PERSONAS.
      // Aquí reutilizamos /api/servicios/usuario/sesion que hace ese mismo
      // JOIN (TPSC_EMPLEADOS ↔ TPSC_PERSONAS) y devuelve nombres separados,
      // cdédula real y descripción del tipo de identificación.
      const usuario = await this.usuarioState.cargar();
      if (!usuario) {
        this.datosProgramador.set(null);
        return;
      }
      this.datosProgramador.set({
        tipoIdentificacion: mapTipoIdentificacion(usuario.tipoIdentificacion),
        numeroIdentificacion: usuario.numeroIdentificacion,
        nombre1: usuario.primerNombre,
        nombre2: usuario.segundoNombre,
        apellido1: usuario.primerApellido,
        apellido2: usuario.segundoApellido,
        nombreCompleto: usuario.nombreCompleto
      });
    } catch (error) {
      if (!environment.production) {
        console.error('Error cargando datos del programador:', error);
      }
    }
  }

  // === Setters para selecciones de búsqueda ===

  setClienteSeleccionado(cliente: {
    nmconsecutivo: number;
    contrato?: string;
    numeroIdentificacion: string;
    nombreComercial: string;
  }): void {
    this.form.nmconsecutivoBeneficiario = cliente.nmconsecutivo;
    this.form.dscontratoBeneficiario = cliente.contrato || '';
    this.form.dsidentificacionBeneficiario = cliente.numeroIdentificacion;
    this.form.dsnombreBeneficiario = cliente.nombreComercial;
    // Limpiar centro de trabajo al cambiar cliente (legacy)
    this.form.nmconsecutivoCentroTrabajo = null;
    this.form.dsnombreCentroTrabajo = '';
  }

  setTareaSeleccionada(tarea: {
    nmconsecutivo: number;
    dsservicio?: string;
    descripcionCompleta?: string;
  }): void {
    this.form.nmconsecutivoTarea = tarea.nmconsecutivo;
    this.form.dsnombreTarea =
      tarea.descripcionCompleta || tarea.dsservicio || '';
  }

  setCentroTrabajoSeleccionado(centro: {
    nmconsecutivo: number;
    dscentroTrabajo?: string;
    descripcionCompleta?: string;
  }): void {
    this.form.nmconsecutivoCentroTrabajo = centro.nmconsecutivo;
    this.form.dsnombreCentroTrabajo =
      centro.descripcionCompleta || centro.dscentroTrabajo || '';
    // Limpiar contacto al cambiar centro de trabajo
    this.form.nmconsecutivoContacto = null;
    this.form.dsnombreContacto = '';
    this.form.dstelefonoContacto = '';
    this.form.dscelularContacto = '';
    this.form.dsemailContacto = '';
  }

  setUnidadNegocioSeleccionada(unidad: UnidadNegocio): void {
    this.form.nmconsecutivoUnidadNegocio = unidad.nmconsecutivo;
    this.form.dsunidadNegocio = unidad.dsunidad || '';
  }

  setProfesionalSeleccionado(profesional: {
    nmconsecutivo?: number;
    dsnombre?: string;
    dscorreo?: string;
  }): void {
    this.form.nmconsecutivoPPR = profesional.nmconsecutivo || null;
    this.form.dsnombrePPR = profesional.dsnombre || '';
    this.form.dscorreoPPR = profesional.dscorreo || '';
  }

  /**
   * Abre el listado de contactos (primera página). Legacy: listarContactosBenefOrden —
   * 1. Si hay centro de trabajo → contactos del centro de trabajo
   * 2. Si no, y hay persona → contactos de la persona
   * 3. Sin ninguno → lista vacía
   *
   * IMPORTANTE: la persona es CDPERSONA (TPSC_PERSONAS), no el consecutivo del empleado:
   * TPSC_PERSONAS_CONTACTOS tiene FK a TPSC_PERSONAS.
   */
  async abrirContactos(origen: {
    nmconsecCt?: number | null;
    cdpersona?: number | null;
  }): Promise<void> {
    this.origenContactos = {
      nmconsecCt: origen.nmconsecCt ?? null,
      cdpersona: origen.cdpersona ?? null
    };
    await this.consultarContactos(() => this.contactosPager.cargarPrimera());
  }

  /** Repite la consulta de la página actual (tras crear o modificar un contacto). */
  async recargarContactos(): Promise<void> {
    await this.consultarContactos(() => this.contactosPager.recargar());
  }

  /**
   * Persona (CDPERSONA) del asesor de la cita: la de la cita existente o, al crear,
   * la del usuario en sesión (que es quien queda como asesor).
   */
  async resolverPersonaAsesor(cdpersonaCita?: number | null): Promise<number | null> {
    if (cdpersonaCita) {
      return cdpersonaCita;
    }
    const usuario = await this.usuarioState.cargar();
    return usuario?.cdpersona ?? null;
  }

  /** Cambia de página: cada acción es una consulta al backend con el cursor vigente. */
  async paginarContactos(accion: AccionPaginacionContactos): Promise<void> {
    await this.consultarContactos(() => this.contactosPager.paginar(accion));
  }

  private async consultarContactos(accion: () => Promise<void>): Promise<boolean> {
    this.loadingContactos.set(true);
    try {
      await accion();
      return true;
    } catch (error) {
      if (!environment.production) {
        console.error('Error cargando contactos:', error);
      }
      this.contactosPager.vaciar();
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se pudieron cargar los contactos'
      });
      return false;
    } finally {
      this.loadingContactos.set(false);
    }
  }

  // === Computed: Título del diálogo ===
  getDialogTitle(): string {
    return this.labels.APPOINTMENT_DIALOG_TITLE;
  }

  // === Computed: Color del estado ===
  getEstadoColor(): string {
    const estado = this._cita?.cdestado;
    if (!estado) return '#6c757d';
    if (estado === ESTADO_CITA.EJECUTADA) return '#28a745';
    if (estado === ESTADO_CITA.CANCELADA) return '#dc3545';
    return '#007bff';
  }

  /**
   * Obtiene configuración de campos según el tipo de cita seleccionado.
   */
  getCamposPorTipo(): Record<string, boolean | number | undefined> {
    const tipoCitaDesdeCita =
      this._cita?.nmconsecutivoTipoCita ||
      this._cita?.tipoCita?.nmconsecutivo ||
      0;
    const tipoCitaDesdeSignal = this.tipoCitaSeleccionado();
    // Legacy onListBoxTypeChange: la selección actual del dropdown manda sobre el
    // tipo persistido, tanto en create como en edit.
    const tipoCitaId = tipoCitaDesdeSignal || tipoCitaDesdeCita;

    return CAMPOS_POR_TIPO_CITA[tipoCitaId] || {};
  }

  /**
   * Obtiene reglas de visibilidad según estado y tipo de cita.
   */
  getVisibility(): CitaVisibilityConfig {
    const c = this._cita;
    const m = this._mode;
    const tipoCitaDesdeCita =
      c?.nmconsecutivoTipoCita || c?.tipoCita?.nmconsecutivo || 0;
    const tipoCitaDesdeSignal = this.tipoCitaSeleccionado();
    // Legacy onListBoxTypeChange -> setVisibleFields(): la selección actual del
    // dropdown manda sobre el tipo persistido.
    const tipoCitaId = tipoCitaDesdeSignal || tipoCitaDesdeCita;
    const configCampos = CAMPOS_POR_TIPO_CITA[tipoCitaId || 0] || {};

    const esCampo = tipoCitaId === TIPOS_CITA_IDS.CAMPO;

    const estado = c?.cdestado || '';
    // Fuente de verdad legacy: TPSC_ESTADOS.SNCITA_MODIFICABLE /
    // SNPERMITE_CANCELACION / SNPERMITE_CUMPLIMIENTO.
    const esEditable = c?.sncitaModificable === 'S' || m === 'create';
    const esCancelable = c?.snpermiteCancelacion === 'S' && m === 'edit';
    const esCumplible = c?.snpermiteCumplimiento === 'S' && m === 'edit';
    const esEjecutada = ESTADOS_EJECUTADOS.some(
      (e) =>
        estado.toUpperCase() === e ||
        c?.dsestado?.toUpperCase().includes('EJECUTAD')
    );

    const visibilidadContactos = this.getVisibilidadContactos();

    return {
      mostrarContactos: visibilidadContactos.mostrarContactos,
      mostrarDireccion: esCampo || !!configCampos.direccion,
      mostrarSincronizacionOutlook: visibilidadContactos.mostrarEnlaceTeams,
      mostrarEnlaceTeams: visibilidadContactos.mostrarEnlaceTeams,
      mostrarBeneficiario: !!configCampos.beneficiario,
      mostrarTarea: !!configCampos.tarea,
      mostrarCentroTrabajo: !!configCampos.centroTrabajo,
      mostrarVirtual: !!configCampos.virtual,
      mostrarGrupoEstudio: !!configCampos.grupoEstudio,
      mostrarFormacionTecnica: !!configCampos.formacionTecnica,
      mostrarInvestigacion: !!configCampos.investigacion,
      mostrarUnidadNegocio: !!configCampos.unidadNegocio,
      mostrarPPR: !!configCampos.ppr,
      observacionObligatoria: !!configCampos.observacionObligatoria,
      ocultarPeriodicidad: !!configCampos.ocultarPeriodicidad,
      mostrarTransporte:
        (tipoCitaId === TIPOS_CITA_IDS.TUTORIA_PEC_PAI ||
          tipoCitaId === TIPOS_CITA_IDS.TUTORIA_CAMPO) &&
        this.virtualSeleccionado() === 'N',
      // Legacy setVisibleScheduledQty(isServiceAppointment()): solo visible cuando
      // el DSUSO_CITA del tipo seleccionado es 'SERVICIO'. Sin excepciones por ID.
      mostrarCantidadProgramada: this.esTipoServicio(),
      mostrarAccordionDetalleOrden: !!c?.nmconsecOrdenServDetalle,
      mostrarAccordionCumplimiento:
        !!c?.nmconsecOrdenServDetalle && esEjecutada,
      mostrarGuardar: m === 'create' || (m === 'edit' && esEditable),
      mostrarCancelar: m === 'edit' && esCancelable,
      // Legacy isFulFillableAppointment(): permite cumplir SIN orden cuando el
      // tipo cita es CAMPO_COMPLEMENTARIO/INFORME_COMPLEMENTARIO.
      // Se exige que TANTO el tipo persistido como el tipo actualmente
      // seleccionado en el dropdown sean complementarios: si difieren, el
      // usuario tiene un cambio sin guardar y `cumplir()` (que lee la cita
      // persistida) operaría sobre un tipo distinto al que ve en pantalla.
      mostrarCumplir:
        m === 'edit' &&
        esCumplible &&
        (!!c?.nmconsecOrdenServDetalle ||
          ((tipoCitaDesdeCita === TIPOS_CITA_IDS.CAMPO_COMPLEMENTARIO ||
            tipoCitaDesdeCita === TIPOS_CITA_IDS.INFORME_COMPLEMENTARIO) &&
            (tipoCitaId === TIPOS_CITA_IDS.CAMPO_COMPLEMENTARIO ||
              tipoCitaId === TIPOS_CITA_IDS.INFORME_COMPLEMENTARIO))),
      mostrarEstado: m !== 'create'
    };
  }

  /**
   * Obtiene reglas de habilitación según estado y modo.
   */
  getHabilitacion(): CitaHabilitacionConfig {
    const m = this._mode;
    const c = this._cita;
    // Legacy: TPSC_ESTADOS.SNCITA_MODIFICABLE.
    const esEditable = c?.sncitaModificable === 'S' || m === 'create';

    const tipoCitaDesdeCita =
      this._cita?.nmconsecutivoTipoCita ||
      this._cita?.tipoCita?.nmconsecutivo ||
      0;
    const tipoCitaDesdeSignal = this.tipoCitaSeleccionado();
    // Selección actual manda; el tipo persistido solo se usa como fallback.
    const tipoCita = tipoCitaDesdeSignal || tipoCitaDesdeCita;
    const duracionFija = tipoCita === TIPOS_CITA_IDS.AUTOAGENDAMIENTO_WHATSAPP;
    // Legacy CrearCitaAdministrativaViewImpl.java:2341 -> slbAppointmentType.setEnabled(false)
    // cuando la cita ya es AUTOAGENDAMIENTO_WHATSAPP (edit/view). En create sí se puede elegir.
    const bloqueoTipoAutoagendamiento =
      m !== 'create' &&
      tipoCitaDesdeCita === TIPOS_CITA_IDS.AUTOAGENDAMIENTO_WHATSAPP;

    return {
      habilitarCampos: m !== 'view' && esEditable,
      // Legacy ServiceAppointmentInterfaceImpl.isEnabledAppointmentType():
      // habilitado en TOKEN_NEW_RECORD o TOKEN_EDIT_RECORD, salvo bloqueo específico
      // para AUTOAGENDAMIENTO_WHATSAPP.
      habilitarTipoCita:
        (m === 'create' || m === 'edit') &&
        esEditable &&
        !bloqueoTipoAutoagendamiento,
      habilitarCantidad: m !== 'view' && esEditable && this.esTipoServicio(),
      habilitarPeriodicidad: m === 'create',
      habilitarGuardar: m !== 'view' && esEditable,
      habilitarDuracion: m !== 'view' && esEditable && !duracionFija
    };
  }

  /**
   * Obtiene accordions activos según visibilidad.
   */
  getActiveAccordionValues(): string[] {
    const visibility = this.getVisibility();
    if (visibility.mostrarAccordionDetalleOrden) {
      return ['detalle-tarea'];
    }
    if (visibility.mostrarAccordionCumplimiento) {
      return ['detalle-cumplimiento'];
    }
    return ['datos-cita'];
  }

  // === Métodos de inicialización ===

  private getFormInicial(): CitaFormModel {
    return {
      nmconsecutivoTipoCita: 0,
      fechaCita: null,
      horaInicio: null,
      duracion: null,
      nmcantidadProgramada: 0,
      dsobservaciones: '',
      nmconsecutivoContacto: null,
      dsnombreContacto: '',
      dstelefonoContacto: '',
      dscelularContacto: '',
      dsemailContacto: '',
      dsdireccion: '',
      tipoperiodicidad: null,
      nmrepeticiones: 1,
      fechaFinPeriodicidad: null,
      snOutlook: 'N',
      snEnlaceTeams: 'N',
      nmconsecutivoBeneficiario: null,
      dscontratoBeneficiario: '',
      dsidentificacionBeneficiario: '',
      dsnombreBeneficiario: '',
      nmconsecutivoTarea: null,
      dsnombreTarea: '',
      nmconsecutivoCentroTrabajo: null,
      dsnombreCentroTrabajo: '',
      nmconsecutivoPPR: null,
      dsnombrePPR: '',
      dscorreoPPR: '',
      dsnombreContactoPPR: '',
      dscorreoContactoPPR: '',
      snvirtual: 'S',
      cdmunicipioOrigen: '',
      dsmunicipioOrigen: '',
      cdmunicipioEntrega: '',
      dsmunicipioEntrega: '',
      cdpoblacion: '',
      dspoblacion: '',
      snpagoTransporteEmpleado: 'N',
      ptvalorTransporte: null,
      snpagoTransporteAdicional: 'N',
      ptvalorTransporteAdicional: null,
      dsobservacionTransporteAdicional: '',
      nmgrupoEstudio: null,
      dslineaEstudio: '',
      dstema: '',
      cdtipoFormacion: null,
      cdtipoInvestigacion: null,
      nmconsecutivoUnidadNegocio: null,
      dsunidadNegocio: '',
      nmconsecOrdenServDetalle: null
    };
  }

  private getTransporteDataInicial(): TransporteData {
    return {
      cdmunicipioOrigen: '',
      dsmunicipioOrigen: '',
      cdsuraOrigen: '',
      cdmunicipioEntrega: '',
      dsmunicipioEntrega: '',
      cdsuraEntrega: '',
      cdpoblacion: '',
      dspoblacion: '',
      snpagoTransporteEmpleado: 'N',
      ptvalorTransporte: null,
      snpagoTransporteAdicional: 'N',
      ptvalorTransporteAdicional: null,
      dsobservacionTransporteAdicional: ''
    };
  }

  /**
   * Muestra el diálogo de confirmación de periodicidad al cargar el formulario.
   */
  mostrarDialogoPeriodicidadSiAplica(): void {
    const c = this._cita;
    if (!c) {
      return;
    }

    // Si ya se mostró y respondió el diálogo para esta cita, no volver a mostrar
    // (evita reset cuando llega el detalle del API y se dispara ngOnChanges de nuevo)
    if (this._periodicidadDialogoRespondido) {
      return;
    }

    const tienePeriodicidad =
      c.nmconsecProgramPeriodica != null && c.nmconsecProgramPeriodica > 0;
    const tienePeriodicidadValida =
      c.tipoperiodicidad && c.tipoperiodicidad.length > 0;
    const esModoEditar = this._mode === 'edit';
    const estado = c.cdestado || '';
    const esEditable = c.sncitaModificable === 'S';
    const esCancelable = c.snpermiteCancelacion === 'S';

    if (
      tienePeriodicidad &&
      (esEditable || esCancelable) &&
      tienePeriodicidadValida &&
      esModoEditar
    ) {
      this.pendienteConfirmPeriodicidad.set(true);
      setTimeout(() => {
        this.confirmationService.confirm({
          header: 'Programación periódica',
          message:
            'Esta cita se encuentra asociada a una programación periódica.\n¿Desea modificar todas las citas futuras asociadas que sean actualizables?',
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: 'Sí',
          rejectLabel: 'No',
          accept: () => {
            this.snModifyPeriodicity = 'S';
            this._periodicidadDialogoRespondido = true;
            this.pendienteConfirmPeriodicidad.set(false);
          },
          reject: () => {
            this.snModifyPeriodicity = 'N';
            this._periodicidadDialogoRespondido = true;
            this.pendienteConfirmPeriodicidad.set(false);
          }
        });
      }, 100);
    }
  }

  /**
   * Carga el formulario con datos de la cita.
   */
  cargarFormulario(): void {
    const c = this._cita;
    if (!c) return;

    // Carga síncrona de datos - no necesita loading
    this.form.nmconsecutivoTipoCita =
      c.nmconsecutivoTipoCita || c.tipoCita?.nmconsecutivo || 0;
    this.tipoCitaSeleccionado.set(this.form.nmconsecutivoTipoCita);

    this.form.dsobservaciones = c.dsobservaciones || '';
    this.form.dsnombreContacto = c.dsnombreContacto || '';
    this.form.dstelefonoContacto = c.dstelefonoContacto || '';
    this.form.dscelularContacto = c.dscelularContacto || '';
    this.form.dsemailContacto = c.dsemailContacto || '';
    this.form.dsdireccion = c.dsdireccion || '';
    this.form.tipoperiodicidad = c.tipoperiodicidad || null;
    this.form.nmrepeticiones = c.nmrepeticiones || c.nmperiodos || 1;
    // Usar nmcantidadProgramada de la cita si existe, sino usar nmhorasCita como fallback
    this.form.nmcantidadProgramada =
      c.nmcantidadProgramada ?? c.nmhorasCita ?? 0;
    this.form.snOutlook = c.snenlaceTeams === 'S' ? 'S' : 'N';
    this.form.snEnlaceTeams = c.snenlaceTeams || 'N';
    this.enlaceTeamsSeleccionado.set(
      this.form.snEnlaceTeams === 'S' ? 'S' : 'N'
    );

    // Orden de servicio asociada (para citas SERVICIO/CAMPO/INFORME)
    this.form.nmconsecOrdenServDetalle = c.nmconsecOrdenServDetalle ?? null;

    this.form.nmconsecutivoBeneficiario = c.nmconsecutivoBenef ?? null;
    this.form.dsnombreBeneficiario = c.nombreBeneficiario || '';
    this.form.nmconsecutivoCentroTrabajo = c.nmconsecBenefCt ?? null;
    this.form.dsnombreCentroTrabajo = c.dscentroTrabajo || '';
    this.form.nmconsecutivoTarea = c.nmconsecutivoTarea ?? c.cdtarea ?? null;
    this.form.dsnombreTarea = c.dstarea || '';

    this.form.nmgrupoEstudio = c.nmgrupoEstudio ?? null;
    this.form.dslineaEstudio = c.dslineaEstudio || '';
    this.form.dstema = c.dstemaEstudio || '';
    this.form.cdtipoFormacion = c.dstipoFormacion || null;
    this.form.cdtipoInvestigacion = c.dsinvestigacionAplicada || null;
    this.form.nmconsecutivoUnidadNegocio = c.nuunidadNegocio ?? null;
    this.form.dsunidadNegocio = c.nuunidadNegocio
      ? `${c.nuunidadNegocio} - ${c.dsunidadNegocio || ''}`
      : '';

    this.form.nmconsecutivoPPR = c.nmconsecProfesional ?? null;
    this.form.dsnombrePPR = c.nombreProfesional || '';
    this.form.dscorreoPPR = c.emailProfesional || '';

    this.form.dscontratoBeneficiario = c.dscontratoBeneficiario || '';
    this.form.dsidentificacionBeneficiario =
      c.dsidentificacionBeneficiario || '';

    this.form.snvirtual = (c as any).snvirtual || 'S';
    this.virtualSeleccionado.set(this.form.snvirtual);

    // Sanea strings tipo " ()" o "()" heredados de una versión previa del SQL
    // (cuando muno.DSMUNICIPIO era NULL y aun así se concatenaba ' (' || dep || ')').
    const sanearMunicipio = (v: string | undefined | null): string => {
      const s = (v ?? '').trim();
      if (!s) return '';
      // Vacío efectivo: " ()", "()", " ( )", etc.
      if (/^\(\s*\)$/.test(s)) return '';
      return s;
    };

    this.transporteData = {
      cdmunicipioOrigen: (c as any).cdmunicipioOrigen || '',
      dsmunicipioOrigen: sanearMunicipio((c as any).dsmunicipioOrigen),
      cdsuraOrigen: (c as any).cdsuraOrigen || '',
      cdmunicipioEntrega: (c as any).cdmunicipioEntrega || '',
      dsmunicipioEntrega: sanearMunicipio((c as any).dsmunicipioEntrega),
      cdsuraEntrega: (c as any).cdsuraEntrega || '',
      cdpoblacion: (c as any).cdpoblacion || '',
      dspoblacion: (c as any).dspoblacion || '',
      snpagoTransporteEmpleado: (c.snpagoTransporteEmpleado || 'N') as
        'S' | 'N',
      ptvalorTransporte: c.ptvalorTransporte ?? null,
      snpagoTransporteAdicional: (c.snTransporteAdicional || 'N') as 'S' | 'N',
      ptvalorTransporteAdicional: c.ptvalorTransporteAdicional ?? null,
      dsobservacionTransporteAdicional: c.dsobservacionesTransporte || ''
    };

    // Sincronizar transporteData con form para validación
    this.form.cdmunicipioOrigen = this.transporteData.cdmunicipioOrigen;
    this.form.dsmunicipioOrigen = this.transporteData.dsmunicipioOrigen;
    this.form.cdmunicipioEntrega = this.transporteData.cdmunicipioEntrega;
    this.form.dsmunicipioEntrega = this.transporteData.dsmunicipioEntrega;
    this.form.cdpoblacion = this.transporteData.cdpoblacion;
    this.form.dspoblacion = this.transporteData.dspoblacion;
    this.form.snpagoTransporteEmpleado =
      this.transporteData.snpagoTransporteEmpleado;
    this.form.ptvalorTransporte = this.transporteData.ptvalorTransporte;
    this.form.snpagoTransporteAdicional =
      this.transporteData.snpagoTransporteAdicional;
    this.form.ptvalorTransporteAdicional =
      this.transporteData.ptvalorTransporteAdicional;
    this.form.dsobservacionTransporteAdicional =
      this.transporteData.dsobservacionTransporteAdicional;

    if (c.feprogramacion) {
      const fecha = new Date(c.feprogramacion);
      this.form.fechaCita = fecha;
      this.form.horaInicio = new Date(fecha);

      const horas = Math.floor(c.nmhorasCita || 1);
      const minutos = Math.round(((c.nmhorasCita || 1) - horas) * 60);
      const duracion = new Date();
      duracion.setHours(horas, minutos, 0, 0);
      this.form.duracion = duracion;
    }
  }

  /**
   * Devuelve true si la cita es "de servicio" según el legacy
   * ServiceAppointmentInterfaceImpl.isServiceAppointment() /
   * CrearCitaAdministrativaViewImpl.isServiceAppointment():
   *
   *   getDto().getNmconsecutivo_tipo_cita().getDsuso_cita()
   *       .equalsIgnoreCase(GeneralParameters.USO_CITA_SERVICIO)
   *
   * Es decir, se evalúa el DSUSO_CITA del tipo de cita seleccionado (catálogo
   * TPSC_TIPOS_DE_CITAS), NO se hardcodean IDs de tipo.
   */
  esTipoServicio(): boolean {
    const tipoCitaId =
      this.form.nmconsecutivoTipoCita ||
      this.tipoCitaSeleccionado() ||
      this._cita?.nmconsecutivoTipoCita ||
      0;
    if (!tipoCitaId) {
      return false;
    }
    return this.obtenerDsusoCita(tipoCitaId)?.toUpperCase() === 'SERVICIO';
  }

  /**
   * DSUSO_CITA del tipo: se busca en el catálogo cargado; si no está, cae al
   * valor persistido en la cita (que trae el JOIN con TPSC_TIPOS_DE_CITAS).
   */
  private obtenerDsusoCita(tipoCitaId: number): string | undefined {
    const tipoCatalogo = this._tiposCita.find(
      (t) => t.nmconsecutivo === tipoCitaId
    );
    return (
      tipoCatalogo?.dsusoCita ??
      (tipoCitaId === (this._cita?.nmconsecutivoTipoCita ?? -1)
        ? this._cita?.dsusoCita
        : undefined)
    );
  }

  /** Visibilidad de Contactos/Teams para el tipo seleccionado (lee signals: apta para computed). */
  getVisibilidadContactos(): VisibilidadContactos {
    const tipoCitaId =
      this.tipoCitaSeleccionado() ||
      this._cita?.nmconsecutivoTipoCita ||
      this._cita?.tipoCita?.nmconsecutivo ||
      0;
    return resolverVisibilidadContactos({
      tipoCitaId,
      dsusoCita: this.obtenerDsusoCita(tipoCitaId),
      snEnlaceTeams: this.enlaceTeamsSeleccionado()
    });
  }

  /**
   * Punto único que aplica la regla de Contactos/Teams al formulario. Se invoca al
   * cambiar el tipo de cita y al cambiar Enlace Teams.
   * - Ajusta Teams al valor que impone la regla (INFORME → 'N').
   * - Si Contactos queda oculto, limpia el contacto: la validación de
   *   obligatorio solo aplica cuando está visible (ver validarCampos).
   */
  actualizarVisibilidadContactos(): void {
    const tipoCitaId = this.form.nmconsecutivoTipoCita;
    const visibilidad = resolverVisibilidadContactos({
      tipoCitaId,
      dsusoCita: this.obtenerDsusoCita(tipoCitaId),
      snEnlaceTeams: this.form.snEnlaceTeams
    });
    this.form.snEnlaceTeams = visibilidad.snEnlaceTeams;
    this.enlaceTeamsSeleccionado.set(visibilidad.snEnlaceTeams);
    if (!visibilidad.mostrarContactos) {
      this.limpiarContacto();
    }
  }

  /**
   * Limpia solo el formulario. Al editar, el contacto ya guardado se conserva igual
   * que en el legado: SP_ACTUALIZAR_CITA aplica
   * nvl(inunmConsectContacto, nmconsecutivo_contacto) y agenda-ms replica ese NVL
   * (AgendaUseCase, consecutivoContacto).
   */
  private limpiarContacto(): void {
    this.form.nmconsecutivoContacto = null;
    this.form.dsnombreContacto = '';
    this.form.dstelefonoContacto = '';
    this.form.dscelularContacto = '';
    this.form.dsemailContacto = '';
  }

  // === Handlers de cambio ===

  onTipoCitaChange(): void {
    this.tipoCitaSeleccionado.set(this.form.nmconsecutivoTipoCita);
    // Al cambiar de tipo, Enlace Teams vuelve a 'N' y se reaplica la regla de Contactos.
    this.form.snEnlaceTeams = 'N';
    this.actualizarVisibilidadContactos();

    if (
      this.form.nmconsecutivoTipoCita ===
      TIPOS_CITA_IDS.AUTOAGENDAMIENTO_WHATSAPP
    ) {
      const duracion = new Date();
      duracion.setHours(2, 0, 0, 0);
      this.form.duracion = duracion;
    }

    if (
      this.form.nmconsecutivoTipoCita ===
      TIPOS_CITA_IDS.DIRECCION_ERRADA_EMPRESA
    ) {
      const duracion = new Date();
      duracion.setHours(0, 30, 0, 0);
      this.form.duracion = duracion;
    }
  }

  onVirtualChange(value: 'S' | 'N'): void {
    this.virtualSeleccionado.set(value);
  }

  /** Legacy validarTeams() (CrearCitaAdministrativaViewImpl L2185-2199). */
  onEnlaceTeamsChange(value: string): void {
    this.form.snEnlaceTeams = value;
    this.actualizarVisibilidadContactos();
  }

  onTransporteDataChange(data: TransporteData): void {
    this.transporteData = data;
    this.form.cdmunicipioOrigen = data.cdmunicipioOrigen;
    this.form.dsmunicipioOrigen = data.dsmunicipioOrigen;
    this.form.cdmunicipioEntrega = data.cdmunicipioEntrega;
    this.form.dsmunicipioEntrega = data.dsmunicipioEntrega;
    this.form.cdpoblacion = data.cdpoblacion;
    this.form.dspoblacion = data.dspoblacion;
    this.form.snpagoTransporteEmpleado = data.snpagoTransporteEmpleado;
    this.form.ptvalorTransporte = data.ptvalorTransporte;
    this.form.snpagoTransporteAdicional = data.snpagoTransporteAdicional;
    this.form.ptvalorTransporteAdicional = data.ptvalorTransporteAdicional;
    this.form.dsobservacionTransporteAdicional =
      data.dsobservacionTransporteAdicional;
  }

  // === Validación ===

  validarCampos(): boolean {
    const validationErrorSummary = 'Error de validación';
    const errors: string[] = [];
    const tipoCita = this.form.nmconsecutivoTipoCita;

    if (!tipoCita) {
      errors.push('Tipo programación');
    }

    if (this._mode === 'create' && !this.form.snEnlaceTeams) {
      errors.push('Enlace de Teams');
    }

    const tiposRequierenBeneficiario: number[] = [
      TIPOS_CITA_IDS.DIRECCION_ERRADA_EMPRESA,
      TIPOS_CITA_IDS.CITA_TENTATIVA,
      TIPOS_CITA_IDS.TUTORIA_CAMPO,
      TIPOS_CITA_IDS.CAMPO_COMPLEMENTARIO,
      TIPOS_CITA_IDS.INFORME_COMPLEMENTARIO,
      TIPOS_CITA_IDS.PROGRAMACION_EN_CLIENTE,
      TIPOS_CITA_IDS.SEGUIMIENTO_INTEGRAL_CLIENTE
    ];
    if (
      tipoCita &&
      tiposRequierenBeneficiario.includes(tipoCita) &&
      !this.form.nmconsecutivoBeneficiario
    ) {
      errors.push('Cliente');
    }

    // PPR (Profesional): para SEGUIMIENTO_INTEGRAL_CLIENTE — debe tener profesional O contacto adicional
    if (tipoCita === TIPOS_CITA_IDS.SEGUIMIENTO_INTEGRAL_CLIENTE) {
      const tieneProfesional = this.form.nmconsecutivoPPR != null;
      const tieneContactoAdicional =
        (this.form.dsnombreContactoPPR?.trim() || '') !== '' ||
        (this.form.dscorreoContactoPPR?.trim() || '') !== '';
      if (!tieneProfesional && !tieneContactoAdicional) {
        errors.push('Profesional o información de contacto adicional');
      }
    }

    const tiposRequierenTarea: number[] = [
      TIPOS_CITA_IDS.CAMPO_COMPLEMENTARIO,
      TIPOS_CITA_IDS.INFORME_COMPLEMENTARIO,
      TIPOS_CITA_IDS.CITA_TENTATIVA,
      TIPOS_CITA_IDS.TUTORIA_CAMPO,
      TIPOS_CITA_IDS.TUTORIA_PEC_PAI
    ];
    if (
      tipoCita &&
      tiposRequierenTarea.includes(tipoCita) &&
      !this.form.nmconsecutivoTarea
    ) {
      errors.push('Nombre Tarea');
    }

    // Contacto oculto (resolverVisibilidadContactos) => no obligatorio.
    const contactosVisibles = resolverVisibilidadContactos({
      tipoCitaId: tipoCita,
      dsusoCita: this.obtenerDsusoCita(tipoCita),
      snEnlaceTeams: this.form.snEnlaceTeams
    }).mostrarContactos;

    if (
      contactosVisibles &&
      this.form.snEnlaceTeams === 'S' &&
      !this.form.dsnombreContacto?.trim()
    ) {
      errors.push('Contacto');
    }

    // Legacy: setRequiredContactInfo(true) cuando es CAMPO, CAMPO_COMPLEMENTARIO, INFORME_COMPLEMENTARIO
    // o cuando isLineaAt() (tipos que muestran beneficiario + tarea + contacto)
    const tiposRequierenContacto: number[] = [
      TIPOS_CITA_IDS.CAMPO,
      TIPOS_CITA_IDS.CAMPO_COMPLEMENTARIO,
      TIPOS_CITA_IDS.INFORME_COMPLEMENTARIO,
      TIPOS_CITA_IDS.PROGRAMACION_EN_CLIENTE
    ];
    if (
      contactosVisibles &&
      tipoCita &&
      tiposRequierenContacto.includes(tipoCita) &&
      this.form.snEnlaceTeams !== 'S' &&
      !this.form.dsnombreContacto?.trim()
    ) {
      errors.push('Contacto');
    }

    if (!this.form.fechaCita) {
      errors.push('Fecha cita (aaaa/mm/dd)');
    }

    if (!this.form.horaInicio) {
      errors.push('Hora de inicio (HH:MM)');
    }

    if (!this.form.duracion) {
      errors.push('Duración');
    }

    // Cantidad programada: requerido para citas de servicio (CAMPO, INFORME y complementarios)
    if (this.esTipoServicio() && !this.form.nmcantidadProgramada) {
      errors.push('Cantidad programada');
    }

    if (
      this.form.tipoperiodicidad &&
      this.form.tipoperiodicidad.trim() !== '' &&
      !this.form.nmrepeticiones
    ) {
      errors.push('Repeticiones');
    }

    if (
      tipoCita &&
      (tipoCita === TIPOS_CITA_IDS.SEGUIMIENTO_INTEGRAL_CLIENTE ||
        tipoCita === TIPOS_CITA_IDS.SITUACION_INESPERADA) &&
      !this.form.dsobservaciones?.trim()
    ) {
      errors.push('Observación');
    }

    if (
      (tipoCita === TIPOS_CITA_IDS.TUTORIA_CAMPO ||
        tipoCita === TIPOS_CITA_IDS.TUTORIA_PEC_PAI) &&
      !this.form.snvirtual
    ) {
      errors.push('Atención virtual');
    }

    if (
      (tipoCita === TIPOS_CITA_IDS.TUTORIA_CAMPO ||
        tipoCita === TIPOS_CITA_IDS.TUTORIA_PEC_PAI) &&
      this.form.snvirtual === 'N'
    ) {
      if (!this.form.cdmunicipioOrigen?.trim())
        errors.push('Municipio de origen');
      if (!this.form.cdmunicipioEntrega?.trim())
        errors.push('Municipio de entrega');
      if (!this.form.cdpoblacion?.trim()) errors.push('Población');
    }

    if (tipoCita === TIPOS_CITA_IDS.GRUPO_ESTUDIO) {
      if (!this.form.nmgrupoEstudio) errors.push('Número del grupo de estudio');
      if (!this.form.dslineaEstudio?.trim()) errors.push('Línea de estudio');
      if (!this.form.dstema?.trim()) errors.push('Tema de estudio');
    }

    if (
      tipoCita === TIPOS_CITA_IDS.FORMACION_TECNICA &&
      !this.form.cdtipoFormacion
    ) {
      errors.push('Tipo de formación');
    }

    if (
      tipoCita === TIPOS_CITA_IDS.INVESTIGACION_APLICADA &&
      !this.form.cdtipoInvestigacion
    ) {
      errors.push('Tipo de investigación');
    }

    if (
      tipoCita === TIPOS_CITA_IDS.CONTENIDOS_TECNICOS &&
      !this.form.dsunidadNegocio?.trim()
    ) {
      errors.push('Unidad de negocio');
    }

    const emailContactoPPR = this.form.dscorreoContactoPPR?.trim() || '';
    if (emailContactoPPR.length > 0 && !REGEX_EMAIL.test(emailContactoPPR)) {
      this.messageService.add({
        severity: 'error',
        summary: validationErrorSummary,
        detail: 'El correo del contacto adicional no tiene un formato válido',
        life: 5000
      });
      return false;
    }

    if (errors.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: validationErrorSummary,
        detail: `Por favor ingrese correctamente los siguientes campos:\n${errors.join('\n')}`,
        life: 5000
      });
      return false;
    }

    const observaciones = this.form.dsobservaciones?.trim() || '';
    if (observaciones.length > MAX_OBSERVACIONES_CITA) {
      this.messageService.add({
        severity: 'error',
        summary: validationErrorSummary,
        detail: `Observación - La longitud máxima permitida es de ${MAX_OBSERVACIONES_CITA} caracteres`,
        life: 5000
      });
      return false;
    }
    if (
      observaciones.length > 0 &&
      !REGEX_ALPHANUMERIC_EXTENDED.test(observaciones)
    ) {
      this.messageService.add({
        severity: 'error',
        summary: validationErrorSummary,
        detail:
          'Observación - El campo alfanumérico no puede contener caracteres especiales',
        life: 5000
      });
      return false;
    }

    // Validar duración según configuración (legacy: CrearCitaAdministrativaViewImpl.java línea 2987-2993)
    if (this.form.duracion) {
      const duracionHoras = redondearCantidad(
        this.form.duracion.getHours() + this.form.duracion.getMinutes() / 60
      );
      const config = this.configuracion();

      // Legacy CrearCitaAdministrativaViewImpl.java:2984 -> duration.compareTo(BigDecimal.ZERO) <= 0
      if (duracionHoras <= 0) {
        this.messageService.add({
          severity: 'error',
          summary: validationErrorSummary,
          detail: 'El valor debe ser mayor que cero (Duración)',
          life: 5000
        });
        return false;
      }

      if (duracionHoras < config.minDuracionHoras) {
        this.messageService.add({
          severity: 'error',
          summary: validationErrorSummary,
          detail: `Valor fuera de rango (${config.minDuracionHoras} - ${config.maxDuracionHoras}) - Duración de la cita`,
          life: 5000
        });
        return false;
      }

      if (duracionHoras > config.maxDuracionHoras) {
        this.messageService.add({
          severity: 'error',
          summary: validationErrorSummary,
          detail: `Valor fuera de rango (${config.minDuracionHoras} - ${config.maxDuracionHoras}) - Duración de la cita`,
          life: 5000
        });
        return false;
      }
    }

    return true;
  }

  /**
   * Formatea una fecha a string YYYY-MM-DD para comparación con festivos.
   * Legacy: DateTimeFormat.getFormat(FORMATS_GENERAL_DATE_YYYY_MM_DD_GUION).format(date)
   */
  private formatDateToYYYYMMDD(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  /**
   * Construye el request para guardar la cita.
   * @param snModifyPeriodicity Opcional - si se pasa, usa este valor; sino usa el valor del Facade
   * @param cita Opcional - cita original para obtener datos del asesor
   */
  buildSaveRequest(
    snModifyPeriodicity?: 'S' | 'N',
    cita?: ProgramacionServicio | null
  ): ProgramacionServicioRequest {
    const citaRef = cita ?? this._cita;
    const fechaHora = this.form.fechaCita
      ? new Date(this.form.fechaCita)
      : new Date();
    if (this.form.horaInicio) {
      fechaHora.setHours(this.form.horaInicio.getHours());
      fechaHora.setMinutes(this.form.horaInicio.getMinutes());
    }

    let duracionHoras = 1;
    if (this.form.duracion) {
      duracionHoras = redondearCantidad(
        this.form.duracion.getHours() + this.form.duracion.getMinutes() / 60
      );
    }

    const formatLocalDateTime = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}`;
    };

    const actualSnModify = snModifyPeriodicity ?? this.snModifyPeriodicity;

    // Legacy CrearCitaAdministrativaViewImpl.java (línea 700 crear y 1135 actualizar):
    // para CAMPO_COMPLEMENTARIO (42) e INFORME_COMPLEMENTARIO (43) el legacy
    // sobrescribe inucantidadprogramada con la duración, ignorando lo digitado en el
    // campo "Cantidad programada":
    //     inputCreate.setInucantidadprogramada(getStpDuration().getDecimalValue());
    // y más abajo (línea 741 crear / 1198 actualizar) envía esa MISMA expresión como
    // inmhorascita, por lo que NMCANTIDAD_PROGRAMADA == NMHORAS_CITA para estos tipos.
    const esCitaComplementaria =
      this.form.nmconsecutivoTipoCita ===
        TIPOS_CITA_IDS.CAMPO_COMPLEMENTARIO ||
      this.form.nmconsecutivoTipoCita ===
        TIPOS_CITA_IDS.INFORME_COMPLEMENTARIO;

    // Para el resto de tipos: cantidad programada del form si está disponible,
    // sino la duración.
    const cantidadProgramada = redondearCantidad(
      esCitaComplementaria
        ? duracionHoras
        : this.form.nmcantidadProgramada > 0
          ? this.form.nmcantidadProgramada
          : duracionHoras
    );

    // Legacy CrearCitaAdministrativaViewImpl.java (líneas 727-731 create y 1183-1187 update):
    // Para AUTOAGENDAMIENTO_WHATSAPP (56) se fuerza CDESTADO = "0019" (CITA_RESERVADA).
    // Para el resto de tipos NO se envía cdestado; el SP asigna el default ("0015" PROGRAMADA).
    const cdestadoForzado =
      this.form.nmconsecutivoTipoCita ===
      TIPOS_CITA_IDS.AUTOAGENDAMIENTO_WHATSAPP
        ? ESTADO_CITA.RESERVADA
        : undefined;

    // Teams oculto (INFORME) viaja siempre 'N' (resolverVisibilidadContactos).
    const visibilidadContactos = resolverVisibilidadContactos({
      tipoCitaId: this.form.nmconsecutivoTipoCita,
      dsusoCita: this.obtenerDsusoCita(this.form.nmconsecutivoTipoCita),
      snEnlaceTeams: this.form.snEnlaceTeams
    });
    const snEnlaceTeams = visibilidadContactos.mostrarEnlaceTeams
      ? this.form.snEnlaceTeams || undefined
      : visibilidadContactos.snEnlaceTeams;

    return {
      dniAsesor: citaRef?.dniAsesor || '',
      nmconsecutivoAsesor: citaRef?.nmconsecutivoAsesor,
      nmconsecutivoTipoCita: this.form.nmconsecutivoTipoCita,
      feprogramacion: formatLocalDateTime(fechaHora),
      nmhorasCita: duracionHoras,
      nmcantidadProgramada: cantidadProgramada,
      dsobservaciones: this.form.dsobservaciones,
      dsdireccion: this.form.dsdireccion,
      nmconsecutivoContacto: this.form.nmconsecutivoContacto ?? undefined,
      nmconsecutivoBenef: this.form.nmconsecutivoBeneficiario ?? undefined,
      nmconsecBenefCt: this.form.nmconsecutivoCentroTrabajo ?? undefined,
      nmconsecutivoTarea: this.form.nmconsecutivoTarea ?? undefined,
      dsnombreContacto: this.form.dsnombreContacto || undefined,
      dstelefonoContacto: this.form.dstelefonoContacto || undefined,
      dscelularContacto: this.form.dscelularContacto || undefined,
      dsemailContacto: this.form.dsemailContacto || undefined,
      tipoperiodicidad: this.form.tipoperiodicidad || undefined,
      nmperiodos: this.form.nmrepeticiones,
      sncitaTeams: snEnlaceTeams,
      emailTeams: snEnlaceTeams === 'S' ? this.form.dsemailContacto : undefined,
      snactualizarcitasfut: actualSnModify,
      nmgrupoEstudio: this.form.nmgrupoEstudio ?? undefined,
      dslineaEstudio: this.form.dslineaEstudio || undefined,
      dstemaEstudio: this.form.dstema || undefined,
      dstipoFormacion: this.form.cdtipoFormacion || undefined,
      dsinvestigacionAplicada: this.form.cdtipoInvestigacion || undefined,
      nuunidadNegocio:
        this.form.nmconsecutivoUnidadNegocio ??
        (this.form.dsunidadNegocio
          ? parseInt(this.form.dsunidadNegocio.split('-')[0].trim(), 10) ||
            undefined
          : undefined),
      nmconsecProfesional: this.form.nmconsecutivoPPR ?? undefined,
      dsnombreContactoPPR: this.form.dsnombreContactoPPR || undefined,
      dscorreoContactoPPR: this.form.dscorreoContactoPPR || undefined,
      snvirtual: this.form.snvirtual || undefined,
      cdmunicipioOrigen: this.form.cdmunicipioOrigen || undefined,
      cdmunicipioEntrega: this.form.cdmunicipioEntrega || undefined,
      cdpoblacion: this.form.cdpoblacion || undefined,
      snpagoTransporteEmpleado: this.form.snpagoTransporteEmpleado || undefined,
      ptvalorTransporte: this.form.ptvalorTransporte ?? undefined,
      snpagoTransporteAdicional:
        this.form.snpagoTransporteAdicional || undefined,
      ptvalorTransporteAdicional:
        this.form.ptvalorTransporteAdicional ?? undefined,
      dsobservacionTransporteAdicional:
        this.form.dsobservacionTransporteAdicional || undefined,
      nmconsecOrdenServDetalle: this.form.nmconsecOrdenServDetalle ?? undefined,
      cdestado: cdestadoForzado
    };
  }

  // === Helpers de formato ===

  formatFecha(fecha?: string): string {
    if (!fecha) return '-';
    try {
      // Parsear directamente el string ISO sin crear Date (evita desfases de timezone)
      const datePart = fecha.includes('T')
        ? fecha.split('T')[0]
        : fecha.substring(0, 10);
      const [year, month, day] = datePart.split('-');
      if (year && month && day) {
        return `${year}/${month.padStart(2, '0')}/${day.padStart(2, '0')}`;
      }
      return fecha;
    } catch {
      return fecha;
    }
  }

  getPeriodicidadLabel(codigo?: string): string {
    if (!codigo) return '--Sin periodicidad--';
    const found = this.periodicidades().find((p) => p.value === codigo);
    return found ? found.label : codigo;
  }

  formatHora(fecha?: string): string {
    if (!fecha) return '-';
    try {
      if (typeof fecha === 'string' && fecha.includes('T')) {
        const timePart = fecha.split('T')[1];
        if (timePart) {
          const [hours, minutes] = timePart.split(':');
          return `${hours.padStart(2, '0')}:${minutes.padStart(2, '0')}`;
        }
      }
      const d = new Date(fecha);
      return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    } catch {
      return '-';
    }
  }

  formatDuracion(horas?: number): string {
    if (!horas) return '-';
    const h = Math.floor(horas);
    const m = Math.round((horas - h) * 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
  }

  formatDecimal(value?: number): string {
    if (value === null || value === undefined) return '-';
    return value.toFixed(2);
  }

  // === Cálculos de periodicidad ===

  calcularFechaFinal(): void {
    if (
      !this.form.fechaCita ||
      !this.form.tipoperiodicidad ||
      !this.form.nmrepeticiones
    ) {
      return;
    }

    const fechaInicio = new Date(this.form.fechaCita);
    const repeticiones = Math.max(0, (this.form.nmrepeticiones || 1) - 1);

    switch (this.form.tipoperiodicidad) {
      case 'D':
        fechaInicio.setDate(fechaInicio.getDate() + repeticiones);
        break;
      case 'S':
        fechaInicio.setDate(fechaInicio.getDate() + repeticiones * 7);
        break;
      case 'M':
        fechaInicio.setMonth(fechaInicio.getMonth() + repeticiones);
        break;
      case 'A':
        fechaInicio.setMonth(fechaInicio.getMonth() + repeticiones * 12);
        break;
    }

    this.form.fechaFinPeriodicidad = fechaInicio;
  }

  calcularRepeticiones(): void {
    if (
      !this.form.fechaCita ||
      !this.form.fechaFinPeriodicidad ||
      !this.form.tipoperiodicidad
    ) {
      return;
    }

    const fechaInicio = new Date(this.form.fechaCita);
    const fechaFin = new Date(this.form.fechaFinPeriodicidad);
    let repeticiones = 0;

    switch (this.form.tipoperiodicidad) {
      case 'D':
        repeticiones = Math.floor(
          (fechaFin.getTime() - fechaInicio.getTime()) / (1000 * 60 * 60 * 24)
        );
        repeticiones++;
        break;
      case 'S':
        repeticiones = Math.floor(
          (fechaFin.getTime() - fechaInicio.getTime()) /
            (1000 * 60 * 60 * 24 * 7)
        );
        repeticiones++;
        break;
      case 'M':
        repeticiones =
          (fechaFin.getFullYear() - fechaInicio.getFullYear()) * 12 +
          (fechaFin.getMonth() - fechaInicio.getMonth());
        break;
      case 'A':
        repeticiones = fechaFin.getFullYear() - fechaInicio.getFullYear();
        break;
    }

    this.form.nmrepeticiones = Math.max(1, repeticiones);
  }

  onPeriodicidadChange(): void {
    if (this.form.tipoperiodicidad && this.form.nmrepeticiones >= 1) {
      this.calcularFechaFinal();
    } else {
      this.form.fechaFinPeriodicidad = null;
    }
  }

  onRepeticionesChange(): void {
    this.calcularFechaFinal();
  }

  onFechaFinalChange(): void {
    this.calcularRepeticiones();
  }

  // === Gestión de contactos ===

  onContactoSeleccionado(contacto: ContactoCentroTrabajo): void {
    this.form.nmconsecutivoContacto = contacto.nmconsecutivo ?? null;
    this.form.dsnombreContacto = contacto.dsnombre;
    this.form.dstelefonoContacto = contacto.dstelefonoFijo || '';
    this.form.dscelularContacto = contacto.dstelefonoCelular || '';
    this.form.dsemailContacto = contacto.dsemail || '';
  }

  /**
   * Crea un nuevo contacto.
   * @param contacto Datos del contacto a crear
   * @param cdpersonaOverride CDPERSONA del beneficiario (para citas de servicio)
   * @param nmconsecCentroTrabajo Consecutivo del centro de trabajo (para asociar contacto al CT)
   */
  async crearContacto(
    contacto: ContactoCentroTrabajo,
    cdpersonaOverride?: number,
    nmconsecCentroTrabajo?: number
  ): Promise<boolean> {
    // Legacy (ListaContactosActivity.crearContacto): la persona es la del origen con el que se
    // abrió el listado (cliente de Línea AT, orden en CAMPO o asesor). No depende de Enlace Teams.
    const cdpersona = cdpersonaOverride;
    if (!cdpersona) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se puede crear contacto sin persona asociada'
      });
      return false;
    }

    this.loadingContactos.set(true);
    try {
      await (this.contactoRepository as any).create(cdpersona, {
        cdpersona: cdpersona,
        dsnombre: contacto.dsnombre,
        dscargo: contacto.dscargo,
        dstelefono: contacto.dstelefonoFijo,
        dscelular: contacto.dstelefonoCelular,
        dsemail: contacto.dsemail,
        nmconsecCentroTrabajo: nmconsecCentroTrabajo
      });

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Los datos se almacenaron correctamente.'
      });

      await this.recargarContactos();
      return true;
    } catch (error) {
      if (!environment.production) {
        console.error('Error al crear contacto:', error);
      }
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: esPersonaNoEncontrada(error)
          ? MENSAJE_PERSONA_DESACTUALIZADA
          : this.obtenerMensajeErrorGuardado(
              error,
              'No se pudo crear el contacto'
            )
      });
      return false;
    } finally {
      this.loadingContactos.set(false);
    }
  }

  /**
   * Modifica un contacto existente.
   * @param contacto Datos del contacto a modificar
   * @param cdpersonaOverride CDPERSONA del beneficiario (para citas de servicio)
   */
  async modificarContacto(
    contacto: ContactoCentroTrabajo,
    cdpersonaOverride?: number
  ): Promise<boolean> {
    // Legacy (modificarContactoPersona): se modifica con la persona dueña del contacto
    const cdpersona = contacto.cdpersona || cdpersonaOverride;
    if (!cdpersona || !contacto.nmconsecutivo) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'No se puede modificar el contacto'
      });
      return false;
    }

    this.loadingContactos.set(true);
    try {
      await (this.contactoRepository as any).update(
        cdpersona,
        contacto.nmconsecutivo,
        {
          cdpersona: contacto.cdpersona || cdpersona,
          dsnombre: contacto.dsnombre,
          dscargo: contacto.dscargo,
          dstelefono: contacto.dstelefonoFijo,
          dscelular: contacto.dstelefonoCelular,
          dsemail: contacto.dsemail
        }
      );

      this.messageService.add({
        severity: 'success',
        summary: 'Éxito',
        detail: 'Los datos se actualizaron correctamente.'
      });

      await this.recargarContactos();
      return true;
    } catch (error) {
      if (!environment.production) {
        console.error('Error al modificar contacto:', error);
      }
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: esPersonaNoEncontrada(error)
          ? MENSAJE_PERSONA_DESACTUALIZADA
          : this.obtenerMensajeErrorGuardado(
              error,
              'No se pudo modificar el contacto'
            )
      });
      return false;
    } finally {
      this.loadingContactos.set(false);
    }
  }

  private obtenerMensajeErrorGuardado(
    error: unknown,
    fallback: string
  ): string {
    const anyError = error as any;
    const cuerpo = anyError?.error;
    const errorAnidado = cuerpo?.error;

    // Prioridad: detail (mensaje seguro específico, contrato ErrorResponse en
    // GlobalExceptionHandler.java) sobre message (categoría genérica).
    return (
      errorAnidado?.detail ||
      errorAnidado?.message ||
      (Array.isArray(cuerpo?.validationErrors)
        ? cuerpo.validationErrors[0]?.message
        : undefined) ||
      (Array.isArray(cuerpo?.errors) ? cuerpo.errors[0]?.message : undefined) ||
      cuerpo?.detail ||
      cuerpo?.message ||
      anyError?.message ||
      fallback
    );
  }

  // === PPR ===

  agregarContactoPPR(): void {
    this.mostrarContactoAdicionalPPR = true;
  }

  eliminarContactoPPR(): void {
    this.mostrarContactoAdicionalPPR = false;
    this.form.dsnombreContactoPPR = '';
    this.form.dscorreoContactoPPR = '';
  }

  // === Nomenclaturas ===

  async cargarNomenclaturasDireccion(): Promise<void> {
    try {
      const [tiposVia, apendices, cardinalidades] = await Promise.all([
        (this.nomenclaturaRepository as any).listarTiposVia(),
        (this.nomenclaturaRepository as any).listarApendices(),
        (this.nomenclaturaRepository as any).listarCardinalidades()
      ]);

      this.tiposVia.set(
        tiposVia.map((t: any) => ({
          codigo: t.codigo,
          descripcion: t.descripcion
        }))
      );
      this.apendices.set(
        apendices.map((a: any) => ({
          codigo: a.codigo,
          descripcion: a.descripcion
        }))
      );
      this.cardinalidades.set(
        cardinalidades.map((c: any) => ({
          codigo: c.codigo,
          descripcion: c.descripcion
        }))
      );
    } catch (error) {
      if (!environment.production) {
        console.error('Error cargando nomenclaturas de dirección:', error);
      }
    }
  }

  // === Reset ===

  resetFormulario(): void {
    this.form = this.getFormInicial();
    this.transporteData = this.getTransporteDataInicial();
    this.tipoCitaSeleccionado.set(0);
    this.virtualSeleccionado.set('S');
    this.enlaceTeamsSeleccionado.set('N');
    this.snModifyPeriodicity = 'N';
    this._periodicidadDialogoRespondido = false;
    this.pendienteConfirmPeriodicidad.set(false);
    this.mostrarContactoAdicionalPPR = false;
  }
}
